import React from "react";

import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardHeader,
  Typography,
} from "@mui/material";
import { LinkRounded, PeopleAlt, PieChart, Place } from "@mui/icons-material";
import { useTheme, alpha } from "@mui/material/styles";

import { AudienceCardMenu } from "./AudienceCardMenu";
import { HelpBox } from "@/components/atoms";
import { Audience } from "@/types";
import { abbreviateNumber, reformatDate } from "@/utils";

interface AudienceCardProps {
  audience: Audience;
  onSelect: (audience: Audience) => void;
  onEditAudience?: (audience: Audience) => void;
  onDeleteAudience?: (audience: Audience) => void;
  onShareAudience?: (audience: Audience) => void;
  onViewInsights?: (audience: Audience) => void;
  onViewExport?: (audience: Audience) => void;
}

const AudienceCard: React.FC<AudienceCardProps> = ({
  audience,
  onSelect,
  onEditAudience,
  onDeleteAudience,
  onShareAudience,
  onViewInsights,
  onViewExport,
}) => {
  const theme = useTheme();
  const sharedOverlay = alpha(theme.palette.primary.main, 0.051);

  const isSharedAudience = audience.shared;
  const numSharedWith =
    (audience.shared_with && audience.shared_with.length) || 0;
  const showMenu =
    onEditAudience || onDeleteAudience || onViewInsights || onShareAudience;

  const handleEditAudience = () => {
    if (onEditAudience) onEditAudience(audience);
  };

  const handleDeleteAudience = () => {
    if (onDeleteAudience) onDeleteAudience(audience);
  };

  const handleShareAudience = () => {
    if (onShareAudience) onShareAudience(audience);
  };

  const handleViewInsights = () => {
    if (onViewInsights) onViewInsights(audience);
  };

  const handleViewExport = () => {
    if (onViewExport) onViewExport(audience);
  };

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        width: "220px",
        backgroundImage: isSharedAudience
          ? `linear-gradient(${sharedOverlay}, ${sharedOverlay})`
          : "var(--Paper-overlay)",
      }}
    >
      <CardActionArea onClick={() => onSelect(audience)}>
        <CardHeader
          avatar={
            isSharedAudience ? (
              <HelpBox title={"This audience is shared with you"}>
                <LinkRounded color="primary" />
              </HelpBox>
            ) : audience.shared_with && numSharedWith > 0 ? (
              <HelpBox
                title={`This audience is shared with ${numSharedWith} user${numSharedWith > 1 ? "s" : ""}`}
              >
                <Avatar
                  variant="rounded"
                  sx={{
                    background: alpha(theme.palette.info.main, 0.5),
                    width: 20,
                    height: 20,
                    fontSize: "12px",
                    m: "2px",
                  }}
                >
                  {numSharedWith}
                </Avatar>
              </HelpBox>
            ) : (
              <Box height={24} />
            )
          }
          sx={{ p: 1, pb: 0 }}
        />
        <CardContent
          sx={{
            height: "120px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-around",
            alignItems: "center",
            pt: 0,
          }}
        >
          <Typography
            variant="h6"
            textAlign="center"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: "2",
              WebkitBoxOrient: "vertical",
            }}
          >
            {audience.audience_name}
          </Typography>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-evenly"
            width="100%"
            mt={2}
          >
            {audience.audience_size && audience.audience_size.length > 1 ? (
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="space-evenly"
              >
                <PieChart color="primary" sx={{ mr: 1 }} />
                <Typography variant="body1" fontWeight={600}>
                  {audience.audience_size.length} segments
                </Typography>
              </Box>
            ) : (
              <>
                {audience.audience_size &&
                  audience.audience_size[0].num_postcodes && (
                    <Box display="flex" flexDirection="row" alignItems="center">
                      <Place fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        {abbreviateNumber(
                          audience.audience_size[0].num_postcodes,
                        )}
                      </Typography>
                    </Box>
                  )}
                {audience.audience_size &&
                  audience.audience_size[0].num_sectors && (
                    <Box display="flex" flexDirection="row" alignItems="center">
                      <Place fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        {abbreviateNumber(
                          audience.audience_size[0].num_sectors,
                        )}
                      </Typography>
                    </Box>
                  )}
                {audience.audience_size &&
                  audience.audience_size[0].population_count && (
                    <Box display="flex" flexDirection="row" alignItems="center">
                      <PeopleAlt fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        {abbreviateNumber(
                          audience.audience_size[0].population_count,
                        )}
                      </Typography>
                    </Box>
                  )}
              </>
            )}
          </Box>
        </CardContent>
      </CardActionArea>
      <CardActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          pl: 2,
        }}
      >
        <Box>
          {audience.created_at && (
            <Typography
              component="div"
              variant="body2"
              color="text.secondary"
              mt={1}
            >
              {reformatDate(audience.created_at)}
            </Typography>
          )}
        </Box>
        {showMenu && (
          <AudienceCardMenu
            audience={audience}
            {...(onEditAudience && { onEditAudience: handleEditAudience })}
            {...(onDeleteAudience && {
              onDeleteAudience: handleDeleteAudience,
            })}
            {...(onShareAudience && { onShareAudience: handleShareAudience })}
            {...(onViewInsights && { onViewInsights: handleViewInsights })}
            {...(onViewExport && { onViewExport: handleViewExport })}
          />
        )}
      </CardActions>
    </Card>
  );
};

export default AudienceCard;
